// Canonical client-side dex totals.
//
// These mirror the Worker's canonical constant (cloudflare/src/lib/constants.ts
// → NATIONAL_DEX_TOTAL). We intentionally re-declare the value here rather than
// importing the server module so the client bundle has no build-time dependency
// on the Worker source tree. Keep the two in sync when a new generation lands.
//
// Server-fed counts always win: any "caught / total" figure should read its
// denominator from a live API response (/api/dex/completion, /api/dex/batches
// stats.total*) when one is available. These constants are the offline / mock
// fallback only, used so demo fixtures and progress bars never divide by an
// undefined value.

/**
 * Total species in the National Pokédex (Gen 1..9, IDs 1..1025). Mirrors
 * the Worker's NATIONAL_DEX_TOTAL. This is the denominator of last resort for
 * completion percentages and the upper bound used by mock generators.
 */
export const NATIONAL_DEX_TOTAL = 1025;

/**
 * Fallback denominator for "released in Pokémon GO" completion bars when the
 * server has not supplied a live total. Previously hardcoded as 914 in two
 * places (friends.ts, data.ts), which produced >100% bars once a real caught
 * count climbed past 914. We default to the National Dex ceiling so the
 * denominator can never be smaller than a legitimate caught count.
 */
export const RELEASED_IN_GO = NATIONAL_DEX_TOTAL;

/**
 * Clamp a caught/total ratio to an integer percentage in [0, 100].
 *
 * The 118% bug came from `Math.round((caught / 914) * 100)` at call sites where
 * `caught` could exceed the hardcoded denominator. Route every progress-bar
 * percentage through here so a stale or undersized denominator can never render
 * a bar wider than full.
 */
export function dexPercent(caught: number, total: number): number {
  if (!Number.isFinite(caught) || !Number.isFinite(total) || total <= 0) return 0;
  const pct = Math.round((caught / total) * 100);
  if (pct < 0) return 0;
  if (pct > 100) return 100;
  return pct;
}
